import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// MUI
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import RestartAlt from '@mui/icons-material/RestartAlt';
import NavigateNext from '@mui/icons-material/NavigateNext';

// common
import MuiLoading from 'common/components/MuiLoading';
import useIsGuest from 'common/hook/useIsGuest';

// screen
import {
  useQuickSearchChannels,
  useQuickSearchClassifications,
  useQuickSearchGroups,
} from 'modules/quick-search/services/useQuickSearchTaxonomy';
import useQuickSearchSuppliers from 'modules/quick-search/services/useQuickSearchSuppliers';
import ChannelChips from 'modules/quick-search/components/ChannelChips';
import CategoryCard from 'modules/quick-search/components/CategoryCard';
import SupplierResultTable from 'modules/quick-search/components/SupplierResultTable';
import type { QsChannel, QsClassification, QsGroup } from 'modules/quick-search/types';

const QuickSearchPage = () => {
  const [sp, setSp] = useSearchParams();
  const channelId = Number(sp.get('kpp')) || undefined;
  const classificationId = Number(sp.get('plhh')) || undefined;
  const groupId = Number(sp.get('nmh')) || undefined;
  const isGuest = useIsGuest();
  // Khách ở bước danh sách HSNL: theo Figma node 7237-28440 màn này đổi tiêu đề và bỏ khối
  // lọc (chip KPP + Xóa bộ lọc + breadcrumb drill) — chỉ còn thanh công cụ và bảng.
  const guestSupplierList = isGuest && !!groupId;

  const { data: channels = [] } = useQuickSearchChannels();
  const { data: classifications = [], isFetching: loadingCls } =
    useQuickSearchClassifications(channelId);
  const { data: groups = [], isFetching: loadingGrp } = useQuickSearchGroups(
    channelId,
    classificationId
  );
  // Tải cả nhóm để Tìm kiếm/Bộ lọc/phân trang client-side (KIOS chưa có param search/filter).
  const { data: suppliersResp, isFetching: loadingSup } = useQuickSearchSuppliers({
    groupId,
    perPage: 100,
  });

  // Cập nhật state lọc trên URL (để "Quay lại" từ chi tiết giữ nguyên bộ lọc).
  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v == null || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSp(next);
  };

  const selectChannel = (ch: QsChannel) =>
    update({ kpp: String(ch.id), plhh: undefined, nmh: undefined, page: undefined });
  const selectClassification = (c: QsClassification) =>
    update({ plhh: String(c.id), nmh: undefined, page: undefined });
  const selectGroup = (g: QsGroup) => update({ nmh: String(g.id), page: undefined });
  const clearFilter = () => setSp(new URLSearchParams());

  const selectedChannel = channels.find((c) => c.id === channelId);
  const selectedClassification = classifications.find((c) => c.id === classificationId);
  const selectedGroup = groups.find((g) => g.id === groupId);

  const renderGrid = (
    items: (QsClassification | QsGroup)[],
    onClick: (item: any) => void,
    loading: boolean,
    renderCount?: (item: any) => ReactNode
  ) => {
    if (loading && items.length === 0) return <MuiLoading />;
    if (items.length === 0)
      return (
        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', p: 2 }}>
          <FormattedMessage id="emptyData" />
        </Typography>
      );
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2,
        }}
      >
        {items.map((item) => (
          <CategoryCard
            key={item.id}
            image={item.image}
            title={item.name}
            description={item.description}
            countLabel={renderCount ? renderCount(item) : undefined}
            onClick={() => onClick(item)}
          />
        ))}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      {/*
        KHÔNG render tiêu đề + breadcrumb ở đây: layout (DefaultBreadcrumb) đã hiện sẵn tiêu đề
        trang "Tìm kiếm nhanh HSNL" và breadcrumb "Cổng thông tin > Tìm kiếm nhanh HSNL" ngay phía
        trên. Thêm ở trong thẻ nữa thành hai tiêu đề và hai breadcrumb chồng nhau.
      */}

      {/* Khối lọc: nút Xóa bộ lọc + hàng chip KPP */}
      {!guestSupplierList && (
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<RestartAlt />}
              onClick={clearFilter}
            >
              <FormattedMessage id="quickSearch.clearFilter" />
            </Button>
          </Box>
          <ChannelChips channels={channels} selectedId={channelId} onSelect={selectChannel} />
        </Box>
      )}

      {/* Breadcrumb drill: KPP > PLHH > NMH (bấm để quay lại cấp trên) */}
      {!guestSupplierList && selectedChannel && (
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mt: 2 }}>
          <Link
            component="button"
            underline="hover"
            color={classificationId ? 'primary' : 'text.primary'}
            onClick={() => update({ plhh: undefined, nmh: undefined, page: undefined })}
          >
            {selectedChannel.name}
          </Link>
          {selectedClassification && (
            <Link
              component="button"
              underline="hover"
              color={groupId ? 'primary' : 'text.primary'}
              onClick={() => update({ nmh: undefined, page: undefined })}
            >
              {selectedClassification.name}
            </Link>
          )}
          {selectedGroup && <Typography color="text.primary">{selectedGroup.name}</Typography>}
        </Breadcrumbs>
      )}

      {/* Nội dung theo cấp đã chọn */}
      <Box sx={{ mt: 2 }}>
        {!channelId && (
          <Typography sx={{ color: 'text.secondary', fontStyle: 'italic', p: 2 }}>
            <FormattedMessage id="quickSearch.selectChannel" />
          </Typography>
        )}
        {channelId &&
          !classificationId &&
          renderGrid(classifications, selectClassification, loadingCls, (c: QsClassification) => (
            <FormattedMessage id="quickSearch.groupCount" values={{ count: c.groupCount }} />
          ))}
        {channelId &&
          classificationId &&
          !groupId &&
          renderGrid(groups, selectGroup, loadingGrp, (g: QsGroup) => (
            <FormattedMessage id="quickSearch.supplierCount" values={{ count: g.supplierCount }} />
          ))}
        {groupId && (
          <SupplierResultTable
            suppliers={suppliersResp?.results ?? []}
            isLoading={loadingSup}
            totalAvailable={suppliersResp?.meta?.total}
          />
        )}
      </Box>
    </Paper>
  );
};

export default QuickSearchPage;
